/**
 * Order data parsing utilities
 */

/**
 * Parse order data from KV storage
 * @param {string|object} value - The raw order data
 * @returns {object|null} Parsed order object or null if invalid
 */
export const parseOrderData = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    
    // Photo order format
    if (parsed?.email && parsed?.items && "total" in parsed && parsed?.url && parsed?.timestamp) {
      return { ...parsed, type: "photo", notes: parsed.notes || "" };
    }
    
    // Gun order format
    if (parsed?.customer && parsed?.items && parsed?.total && parsed?.timestamp) {
      // Check for dog tag items
      const dogTagItem = parsed.items.find(item => 
        typeof item === 'string' && 
        item.includes('Dog Tag:')
      );
      if (dogTagItem) {
        // Extract CSV data from dog tag item
        parsed.info = dogTagItem.split('Dog Tag:')[1]?.trim();
      }
      return { ...parsed, type: 'gun', notes: parsed.notes || '' };
    }
    
    return null;
  } catch {
    return null;
  }
};

/**
 * Check if an order is a valid photo order
 * @param {object} order - The order object
 * @returns {boolean} True if valid photo order
 */
export const isPhotoOrder = (order) => {
  return order?.type === "photo" && 
         order?.email && 
         order?.items && 
         "total" in order && 
         order?.url && 
         order?.timestamp;
};

/**
 * Check if an order is a valid gun order
 * @param {object} order - The order object
 * @returns {boolean} True if valid gun order
 */
export const isGunOrder = (order) => {
  return order?.type === "gun" && 
         order?.customer && 
         order?.items && 
         order?.total && 
         order?.timestamp;
};

/**
 * Extract dog tag data from order items
 * @param {array} items - Order items array
 * @returns {string|null} CSV data or null if no dog tags
 */
export const extractDogTagData = (items) => {
  const dogTagItem = items?.find(item => 
    typeof item === 'string' && item.includes('Dog Tag:')
  );
  return dogTagItem ? dogTagItem.split('Dog Tag:')[1]?.trim() : null;
};

/**
 * Get order type based on order data
 * @param {object} order - The order object
 * @returns {string} 'photo', 'gun', or 'unknown'
 */
export const getOrderType = (order) => {
  if (isPhotoOrder(order)) return 'photo';
  if (isGunOrder(order)) return 'gun';
  return 'unknown';
};